// ════════════════════════════════════════════════════════════════════════════
//  Udisys Gestor Web  —  src/lib/tipoEmpresa.ts
//
//  Ramo de atividade da empresa (cad_empresa.id_tipo_empresa) e as regras de
//  quais menus cada ramo vê. Cópia do Desktop:
//    Core/Udisys.Cross/Licenciamento/TipoEmpresa.cs           (códigos)
//    Desktop/UdisysGestor/Menus/RegrasVisibilidadeBotoes.cs   (catálogo)
//  Mesma semântica de lá: item que NÃO está no catálogo aparece para todos os
//  ramos; o catálogo só restringe. A permissão do perfil continua valendo à
//  parte (a rota é protegida pelo ProtectedRoute).
//  Ao alterar uma regra aqui, conferir se o Desktop mudou junto.
// ════════════════════════════════════════════════════════════════════════════

export const TipoEmpresa = {
  Varejo: 0,
  ServicoGeral: 1,
  Restaurante: 2,
  Industria: 3,
  ServicoOficina: 4,
  Transportadora: 5,
  Farmacia: 6,
} as const;

export type TipoEmpresaId = (typeof TipoEmpresa)[keyof typeof TipoEmpresa];

export const TIPOS_EMPRESA: { value: TipoEmpresaId; label: string }[] = [
  { value: TipoEmpresa.Varejo,         label: "Varejo" },
  { value: TipoEmpresa.ServicoGeral,   label: "Serviço Geral" },
  { value: TipoEmpresa.Restaurante,    label: "Restaurante / Panificadora" },
  { value: TipoEmpresa.Industria,      label: "Indústria" },
  { value: TipoEmpresa.ServicoOficina, label: "Serviço Oficina" },
  { value: TipoEmpresa.Transportadora, label: "Transportadora" },
  { value: TipoEmpresa.Farmacia,       label: "Farmácia" },
];

const {
  Varejo, ServicoGeral, Restaurante, Industria, ServicoOficina, Transportadora, Farmacia,
} = TipoEmpresa;

/** URL do menu → ramos que podem vê-la (o resto vê tudo). */
const REGRAS_MENU: Record<string, readonly TipoEmpresaId[]> = {
  // TRANSPORTADORA — só CT-e e CT-e OS são exclusivos (MDF-e, averbação = todos)
  "/fiscal/cte":    [Transportadora],
  "/fiscal/cte-os": [Transportadora],

  // SERVIÇO + OFICINA + VAREJO — status de OS (padaria → buffet)
  "/operacional/status-os": [ServicoGeral, ServicoOficina, Varejo],

  // INDÚSTRIA + RESTAURANTE — produção (ficha técnica, etapas, PCP, MRP)
  "/operacional/pcp":              [Industria, Restaurante],
  "/operacional/fichas-tecnicas":  [Industria, Restaurante],
  "/operacional/etapas-producao":  [Industria, Restaurante],
  "/operacional/planejamento-mrp": [Industria, Restaurante],

  // FARMÁCIA — SNGPC ANVISA
  "/operacional/sngpc-receitas":     [Farmacia],
  "/operacional/sngpc-transmissoes": [Farmacia],

  // PROJETOS & SERVIÇOS — só prestação de serviço (S / O)
  "/operacional/projetos":            [ServicoGeral, ServicoOficina],
  "/operacional/apontamento-horas":   [ServicoGeral, ServicoOficina],
  "/operacional/aprovacao-timesheet": [ServicoGeral, ServicoOficina],

  // MERCADO LIVRE — todos exceto Transportadora
  "/integracao/mercadolivre": [Varejo, ServicoGeral, Restaurante, Industria, ServicoOficina, Farmacia],
};

/**
 * O item de menu `url` aparece para o ramo `tipo`?
 * `tipo` undefined = ramo ainda carregando → esconde só os itens restritos
 * (evita o SNGPC "piscar" para quem não é farmácia).
 * `tipo` null = não deu para saber o ramo (API antiga/erro) → mostra tudo,
 * como era antes desta regra existir.
 */
export function menuVisivelParaTipo(url: string, tipo: number | null | undefined): boolean {
  const permitidos = REGRAS_MENU[url];
  if (!permitidos) return true;
  if (tipo === null) return true;
  if (tipo === undefined) return false;
  return (permitidos as readonly number[]).includes(tipo);
}

export const ehFarmacia = (tipo: number | null | undefined) => tipo === Farmacia;
